// Idle lock (system/lock--idle, decision ردّ ٦): after 15 minutes without
// input the screen dims + blurs and a PIN re-authenticates the SAME session —
// nothing underneath unmounts (an open invoice is not lost) and there is no
// automatic sign-out. «تبديل المستخدم» signs out for a colleague.
//
// The locked flag is persisted so a reload while locked stays locked (a reload
// must never be a way around the lock). An expired session that cannot be
// renewed silently (api.ts 401 path) also locks, with reason 'expired'.

import { toArabicDigits } from './digits';
import { readStore, writeStore } from './storage';

/** Default idle lock (HANDOFF §4: 15 minutes). */
export const IDLE_LOCK_MS = 15 * 60 * 1000;

/**
 * The idle lock for this session: the server's `idle_lock_seconds` when the
 * strategy reported it (node: erp-core LOCAL_IDLE_LOCK_MINUTES, returned by
 * login/unlock), else the 15-minute default (cloud).
 */
export function idleLockMs(serverSeconds: number | null | undefined): number {
  return serverSeconds && serverSeconds > 0 ? serverSeconds * 1000 : IDLE_LOCK_MS;
}

/** «مقفل بعد ١٥ دقيقة» — built from the actual idle lock, Arabic-Indic digits. */
export function lockedAfterTitle(ms: number): string {
  const min = Math.max(1, Math.round(ms / 60_000));
  if (min === 1) return 'مقفل بعد دقيقة';
  if (min === 2) return 'مقفل بعد دقيقتين';
  const unit = min >= 3 && min <= 10 ? 'دقائق' : 'دقيقة';
  return `مقفل بعد ${toArabicDigits(min)} ${unit}`;
}
export type LockReason = 'idle' | 'expired' | 'manual';

const KEY = 'erp.locked';
type Listener = () => void;
const listeners = new Set<Listener>();

let locked: LockReason | null = (readStore(KEY) as LockReason | null) || null;

function emit() {
  listeners.forEach((l) => l());
}

export const lockStore = {
  isLocked: (): boolean => locked != null,
  reason: (): LockReason | null => locked,
  lock(reason: LockReason = 'manual'): void {
    if (locked === reason) return;
    locked = reason;
    writeStore(KEY, reason);
    emit();
  },
  unlock(): void {
    if (locked == null) return;
    locked = null;
    writeStore(KEY, null);
    emit();
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove', 'scroll'] as const;

export interface IdleWatcher {
  /** Restart the countdown (called on every activity event). */
  poke(): void;
  stop(): void;
}

/**
 * Watches user activity on `target` and calls lockStore.lock('idle') after
 * `timeoutMs` of silence. Activity while locked does not unlock (only the PIN does).
 */
export function startIdleWatch(opts: { timeoutMs?: number; target?: Window | Document } = {}): IdleWatcher {
  const timeoutMs = opts.timeoutMs ?? IDLE_LOCK_MS;
  const target = opts.target ?? window;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastPoke = 0;

  const arm = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => lockStore.lock('idle'), timeoutMs);
  };
  const poke = () => {
    if (lockStore.isLocked()) return;
    const now = Date.now();
    // mousemove/scroll fire constantly; re-arming at most once a second is plenty.
    if (now - lastPoke < 1000 && timer) return;
    lastPoke = now;
    arm();
  };
  for (const e of ACTIVITY_EVENTS) target.addEventListener(e, poke, { passive: true });
  const offLock = lockStore.subscribe(() => {
    if (!lockStore.isLocked()) {
      lastPoke = 0;
      arm();
    }
  });
  arm();

  return {
    poke,
    stop() {
      if (timer) clearTimeout(timer);
      timer = null;
      offLock();
      for (const e of ACTIVITY_EVENTS) target.removeEventListener(e, poke);
    },
  };
}
